import React, { useEffect, useState } from 'react';
import { Basic, Premium } from '../common/Profiles';
import MatchmakingDialog from '../common/Dialogs/MatchmakingDialog';
import {
  DIALOG_CONTEXT,
  PROFILES_INFO,
  createMatchMakingUser,
  deactivateMatchMakingUser,
  editPasswordMatchMakingUser,
  listMatchMakingUsers,
} from '../common/utils';
import { useMatchMakingStore } from '../common/store';
import UserList from '../common/UserList';
import { useIntl } from 'react-intl';
import translations from 'utils/translations';
import { useNetwork } from 'services/network';
import { useContext } from 'react';
import { LoaderCountContext } from 'context/LoaderV5';

const DIALOG_DEFAULT_VALUES = {
  isOpen: false,
  onClose: () => {},
  onAgree: () => {},
  context: '',
  askFor: '',
};

const UsersTab = () => {
  // Hooks
  const intl = useIntl();
  const i18nAlerts = translations.MatchMakingV2(intl).Alerts;
  const i18nMap = translations.MatchMakingV2(intl).MappedErrors;
  const network = useNetwork();
  const { setLoaderActive } = useContext(LoaderCountContext);
  const [users, currentProfile, loggedUser] = useMatchMakingStore((state) => [
    state.users,
    state.currentProfile,
    state.loggedUser,
  ]);

  // States
  const [dialogData, setDialogData] = useState(DIALOG_DEFAULT_VALUES);

  // Effects
  useEffect(() => {
    const fetchMatchMakingUsers = async () => {
      try {
        showLoader();
        const { users: usersRes } = await listMatchMakingUsers({
          network,
          id: loggedUser.federationId,
        });
        storeMatchMakingUsers(usersRes);
      } catch (error) {
        storeMatchMakingUsers([]);
      } finally {
        hideLoader();
      }
    };
    if (!users && loggedUser?.federationId) {
      fetchMatchMakingUsers();
    }
  }, [loggedUser]);

  // Functions
  const showLoader = () => setLoaderActive(true);
  const hideLoader = () => setLoaderActive(false);
  const storeMatchMakingUsers = (array) => {
    useMatchMakingStore.setState({
      users: array,
    });
  };
  const emptyDialog = () => setDialogData(DIALOG_DEFAULT_VALUES);

  const closeAlert = (sev) =>
    useMatchMakingStore.setState({ alertData: { severity: sev } });

  const openAlert = ({ title, message = '', sev }) => {
    useMatchMakingStore.setState({
      alertData: {
        title,
        message,
        onClose: () => closeAlert(sev),
        severity: sev,
      },
    });
  };

  /**
   *  INFO FLOW
   */
  // const handleAskForInfo = (askFor) => {
  //   if (askFor === ASK_FOR.GOLD) {
  //     console.log('HANDLE ASK INFOR FOR GOLD::');
  //   } else {
  //     console.log('HANDLE ASK INFOR FOR SILVER::');
  //   }
  //   emptyDialog();
  // };

  // const openInfoDialog = (askFor) => {
  //   setDialogData({
  //     isOpen: true,
  //     context: DIALOG_CONTEXT.INFO,
  //     onClose: emptyDialog,
  //     onAgree: () => handleAskForInfo(askFor),
  //     askFor,
  //   });
  // };

  /**
   * CREATE USER FLOW
   */
  const handleCreateUser = async (createInput) => {
    try {
      emptyDialog();
      showLoader();
      const data = {
        lastName: createInput.name,
        newPassword: createInput.password,
        ragioneSociale: loggedUser.name,
      };
      const createRes = await createMatchMakingUser({
        network,
        id: loggedUser?.federationId,
        data,
      });
      if (createRes?.success) {
        const newUser = {
          federationIdentifier: createRes.federationIdentifier || createRes.id,
          name: createInput.name,
          email: createRes.email?.toLowerCase() || '',
        };
        useMatchMakingStore.setState((prev) => ({
          users: [...prev.users, newUser],
        }));
        openAlert({
          title: `${i18nAlerts.create.title} ${
            createRes.email?.toLowerCase() || ''
          }`,
          sev: 'success',
        });
      } else {
        openAlert({
          title: i18nMap?.[createRes?.errorMessage?.[0]] || i18nMap.Code11,
          sev: 'error',
        });
      }
    } catch (error) {
      openAlert({ title: i18nMap.Code11, sev: 'error' });
    } finally {
      hideLoader();
    }
  };

  const openCreateDialog = () => {
    setDialogData({
      isOpen: true,
      context: DIALOG_CONTEXT.CREATE,
      onClose: emptyDialog,
      onAgree: (createInput) => handleCreateUser(createInput),
    });
  };

  /**
   * EDIT USER PASSWORD FLOW
   */
  const handleEditUserPassword = async (editInput, userId) => {
    try {
      emptyDialog();
      showLoader();
      const data = {
        newPassword: editInput.password,
        userFederationId: userId,
      };
      const editeRes = await editPasswordMatchMakingUser({
        network,
        id: loggedUser?.federationId,
        data,
      });
      if (editeRes?.success) {
        openAlert({ title: i18nAlerts.edit.title, sev: 'success' });
      } else {
        openAlert({
          title: i18nMap?.[editeRes?.errorMessage?.[0]] || i18nMap.Code11,
          sev: 'error',
        });
      }
    } catch (error) {
      openAlert({ title: i18nMap.Code11, sev: 'error' });
    } finally {
      hideLoader();
    }
  };

  const openEditDialog = (userId) => {
    const selectedUser = users.find((u) => u.federationIdentifier === userId);
    setDialogData({
      isOpen: true,
      context: DIALOG_CONTEXT.EDIT,
      onClose: emptyDialog,
      onAgree: (editInput) => handleEditUserPassword(editInput, userId),
      userName: selectedUser?.name || '',
    });
  };

  /**
   * DELETE FLOW
   */
  const handleRemoveUser = async (userId) => {
    try {
      emptyDialog();
      showLoader();
      const data = {
        userFederationId: userId,
      };
      const deleteRes = await deactivateMatchMakingUser({
        network,
        id: loggedUser?.federationId,
        data,
      });
      if (deleteRes?.success) {
        useMatchMakingStore.setState((prev) => ({
          users: [
            ...prev.users.filter((u) => u.federationIdentifier !== userId),
          ],
        }));
        openAlert({
          title: i18nAlerts.delete.title,
          message: i18nAlerts.delete.message,
          sev: 'success',
        });
      } else {
        openAlert({
          title: i18nMap?.[deleteRes?.errorMessage?.[0]] || i18nMap.Code11,
          sev: 'error',
        });
      }
    } catch (error) {
      openAlert({ title: i18nMap.Code11, sev: 'error' });
    } finally {
      hideLoader();
    }
  };

  const openDeleteDialog = (userId) => {
    setDialogData({
      isOpen: true,
      context: DIALOG_CONTEXT.DELETE,
      onClose: emptyDialog,
      onAgree: () => handleRemoveUser(userId),
    });
  };

  return users ? (
    <>
      {users?.length ? (
        <UserList
          onCreate={openCreateDialog}
          handleDeleteUser={openDeleteDialog}
          handleResetUserPassword={openEditDialog}
        />
      ) : (
        <>
          {currentProfile?.id === PROFILES_INFO.PREMIUM.id ? (
            <Premium
              profile={loggedUser}
              currentProfile={currentProfile}
              onCreate={openCreateDialog}
            />
          ) : (
            <Basic profile={loggedUser} currentProfile={currentProfile} />
          )}
        </>
      )}
      <MatchmakingDialog data={dialogData} />
    </>
  ) : (
    <></>
  );
};
export default UsersTab;
